import DescriptionDetails from "components/DescriptionDetails";
import FormFieldTimestamp from "components/FormFields/Timestamp";
import FormFieldViewText from "components/FormFields/ViewText";
import { ExclamationMarkTriangleFilled } from "components/icons/generated";
import Box from "ds/components/Box";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import DrawerSimple from "ds/components/DrawerNew/Simple";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import { useShowFullDescriptionWithBackArrow } from "ds/components/FullDescriptionDrawer/useShowFullDescriptionWithBackArrow";
import Icon from "ds/components/Icon";
import Typography from "ds/components/Typography";
import { isApiKeyExpired } from "shared/ApiKey/helpers";
import { ApiKey } from "types/generated";
import { formatDate } from "utils/date";
import { unixTimestampToMs } from "utils/time";
import ApiKeyExpiresAt from "views/Account/Settings/ApiKey/components/ExpiresAt";

type PersonalSettingsApiKeysDetailsDrawerProps = {
  apiKey: Pick<ApiKey, "id" | "name" | "lastUsedAt" | "expiresAt" | "description">;
};

const PersonalSettingsApiKeysDetailsDrawer = createDrawer(
  ({ apiKey }: PersonalSettingsApiKeysDetailsDrawerProps) => {
    const onOpenFullDescription = useShowFullDescriptionWithBackArrow(apiKey.description);
    const isExpired = isApiKeyExpired(apiKey.expiresAt);

    return (
      <DrawerSimple isDismissable>
        <DrawerHeader justify="between">
          <DrawerHeaderTitle title="API key details" />
          <DrawerCloseIcon />
        </DrawerHeader>
        <DrawerBody>
          <FormFieldViewText label="Name" value={apiKey.name} />
          <FormFieldViewText label="Key ID" value={apiKey.id} withCopy />
          {apiKey.lastUsedAt && (
            <FormFieldTimestamp label="Last used" timestamp={apiKey.lastUsedAt} />
          )}
          {!apiKey.lastUsedAt && (
            <FormField label="Last used">
              <Typography tag="span" variant="p-body3">
                Never used
              </Typography>
            </FormField>
          )}
          <FormField label="Expires at" align="start">
            {isExpired && apiKey.expiresAt && (
              <Box direction="row" align="center" gap="small">
                <Icon src={ExclamationMarkTriangleFilled} color="warning" noShrink />
                <Typography tag="span" variant="p-body3" color="warning">
                  {`Expired on ${formatDate({ date: unixTimestampToMs(apiKey.expiresAt) })}`}
                </Typography>
              </Box>
            )}
            {!isExpired && <ApiKeyExpiresAt expiresAt={apiKey.expiresAt} />}
          </FormField>
          <DescriptionDetails
            description={apiKey.description}
            onOpenFullDescription={onOpenFullDescription}
          />
        </DrawerBody>
      </DrawerSimple>
    );
  }
);

export const showPersonalSettingsApiKeysDetailsDrawer = createDrawerTrigger(
  PersonalSettingsApiKeysDetailsDrawer
);
